/**
 * Estate Flow - Promo Code Manager (Admin)
 * Manage promo codes for setup fees, subscriptions, or both
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Copy, CheckCircle, XCircle, Loader2, Ticket, DollarSign, Percent, CreditCard, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromoCodeManager = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    max_uses: -1,
    discount_type: 'percentage',
    discount_value: 50,
    applies_to: 'both',
    duration_months: 0
  });

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/promo/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPromoCodes(response.data.promo_codes || []);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const generateCode = () => {
    const prefixes = ['SAVE', 'DEAL', 'VIP', 'PROMO', 'OFFER'];
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const randomCode = prefix + Math.random().toString(36).substring(2, 6).toUpperCase();
    setFormData({...formData, code: randomCode});
  };

  const createPromoCode = async () => {
    try {
      if (!formData.code.trim()) {
        toast.error('Promo code is required');
        return;
      }

      if (!formData.description.trim()) {
        toast.error('Description is required');
        return;
      }

      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/promo/generate`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Promo code created successfully!');
        setShowCreateDialog(false);
        setFormData({
          code: '',
          description: '',
          max_uses: -1,
          discount_type: 'percentage',
          discount_value: 50,
          applies_to: 'both',
          duration_months: 0
        });
        fetchPromoCodes();
      } else {
        toast.error(response.data.message || 'Failed to create promo code');
      }
    } catch (error) {
      console.error('Error creating promo code:', error);
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const deactivateCode = async (code) => {
    if (!window.confirm(`Are you sure you want to deactivate promo code "${code}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/promo/deactivate`,
        { code },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Promo code deactivated');
      fetchPromoCodes();
    } catch (error) {
      console.error('Error deactivating promo code:', error);
      toast.error('Failed to deactivate promo code');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Promo code copied to clipboard!');
  };

  const getDiscountDisplay = (promo) => {
    if (promo.discount_type === 'free') {
      return 'FREE';
    } else if (promo.discount_type === 'percentage') {
      return `${promo.discount_value}% OFF`;
    } else if (promo.discount_type === 'fixed_amount') {
      return `$${promo.discount_value} OFF`;
    }
    return promo.discount_value;
  };

  const getAppliesToDisplay = (applies_to) => {
    switch(applies_to) {
      case 'setup_fee': return 'Setup Fee Only';
      case 'subscription': return 'Subscription Only';
      case 'both': return 'Setup + Subscription';
      default: return 'Both';
    }
  };

  const getAppliesToBadgeColor = (applies_to) => {
    switch(applies_to) {
      case 'setup_fee': return 'bg-blue-100 text-blue-800';
      case 'subscription': return 'bg-purple-100 text-purple-800';
      case 'both': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            <Ticket className="h-6 w-6 mr-2 text-purple-600" />
            Promo Code Manager
          </h2>
          <p className="text-gray-600 mt-1">Create and manage promotional codes for discounts</p>
        </div>

        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700">
              <Plus className="h-4 w-4 mr-2" /> Create Promo Code
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Promo Code</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Promo Code */}
              <div>
                <Label htmlFor="code">Promo Code *</Label>
                <div className="flex space-x-2">
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="SAVE50"
                    className="font-mono"
                  />
                  <Button variant="outline" onClick={generateCode}>
                    Generate
                  </Button>
                </div>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="50% Off Launch Special"
                />
              </div>

              {/* Applies To - NEW */}
              <div>
                <Label htmlFor="applies_to">Applies To *</Label>
                <Select 
                  value={formData.applies_to}
                  onValueChange={(value) => setFormData({...formData, applies_to: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="setup_fee">
                      <div className="flex items-center">
                        <CreditCard className="h-4 w-4 mr-2 text-blue-600" />
                        Setup Fee Only
                      </div>
                    </SelectItem>
                    <SelectItem value="subscription">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-purple-600" />
                        Subscription Only
                      </div>
                    </SelectItem>
                    <SelectItem value="both">
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 mr-2 text-emerald-600" />
                        Both (Setup + Subscription)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose what the discount applies to
                </p>
              </div>

              {/* Discount Type */}
              <div>
                <Label htmlFor="discount_type">Discount Type *</Label>
                <Select 
                  value={formData.discount_type}
                  onValueChange={(value) => {
                    let defaultValue = 50;
                    if (value === 'free') defaultValue = 100;
                    if (value === 'fixed_amount') defaultValue = 100;
                    setFormData({...formData, discount_type: value, discount_value: defaultValue});
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">
                      <div className="flex items-center">
                        <Percent className="h-4 w-4 mr-2" />
                        Percentage Discount (e.g., 50% off)
                      </div>
                    </SelectItem>
                    <SelectItem value="fixed_amount">
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 mr-2" />
                        Fixed Amount (e.g., $100 off)
                      </div>
                    </SelectItem>
                    <SelectItem value="free">
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2 text-emerald-600" />
                        FREE (100% off)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Discount Value */}
              {formData.discount_type !== 'free' && (
                <div>
                  <Label htmlFor="discount_value">
                    {formData.discount_type === 'percentage' ? 'Percentage Off' : 'Amount Off ($)'}
                  </Label>
                  <div className="flex items-center">
                    <Input
                      id="discount_value"
                      type="number"
                      min="1"
                      max={formData.discount_type === 'percentage' ? 100 : 10000}
                      value={formData.discount_value}
                      onChange={(e) => setFormData({...formData, discount_value: parseFloat(e.target.value) || 0})}
                    />
                    <span className="ml-2 text-gray-500">
                      {formData.discount_type === 'percentage' ? '%' : 'USD'}
                    </span>
                  </div>
                </div>
              )}

              {/* Duration (for subscriptions) */}
              {(formData.applies_to === 'subscription' || formData.applies_to === 'both') && (
                <div>
                  <Label htmlFor="duration_months">Discount Duration</Label>
                  <Select 
                    value={String(formData.duration_months)}
                    onValueChange={(value) => setFormData({...formData, duration_months: parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Forever (Lifetime)</SelectItem>
                      <SelectItem value="1">First Month Only</SelectItem>
                      <SelectItem value="3">First 3 Months</SelectItem>
                      <SelectItem value="6">First 6 Months</SelectItem>
                      <SelectItem value="12">First 12 Months</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    How long the subscription discount lasts
                  </p>
                </div>
              )}

              {/* Max Uses */}
              <div>
                <Label htmlFor="max_uses">Maximum Uses</Label>
                <Input
                  id="max_uses"
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({...formData, max_uses: parseInt(e.target.value) || -1})}
                  placeholder="-1 for unlimited"
                />
                <p className="text-xs text-gray-500 mt-1">
                  -1 = Unlimited uses
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button className="bg-purple-600 hover:bg-purple-700" onClick={createPromoCode}>
                Create Promo Code
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Promo Codes List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Active Promo Codes</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : promoCodes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Ticket className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No promo codes yet. Create your first one!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {promoCodes.map((promo) => (
                <div 
                  key={promo.code} 
                  className="flex items-center justify-between p-4 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center">
                      <code className="text-lg font-bold bg-white px-3 py-1 rounded border">
                        {promo.code}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(promo.code)}
                        className="ml-1"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <div>
                      <p className="font-medium">{promo.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className="bg-purple-100 text-purple-800">
                          {getDiscountDisplay(promo)}
                        </Badge>
                        <Badge className={getAppliesToBadgeColor(promo.applies_to)}>
                          {getAppliesToDisplay(promo.applies_to)}
                        </Badge>
                        {promo.duration_months > 0 && (
                          <Badge className="bg-amber-100 text-amber-800">
                            {promo.duration_months} month{promo.duration_months > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-500">
                        Uses: {promo.current_uses}/{promo.max_uses === -1 ? '∞' : promo.max_uses}
                      </p>
                      {promo.is_active ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" /> Active
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800">
                          <XCircle className="h-3 w-3 mr-1" /> Inactive
                        </Badge>
                      )}
                    </div>
                    
                    {promo.is_active && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deactivateCode(promo.code)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Deactivate
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Create Suggestions */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <CardHeader>
          <CardTitle className="text-lg">💡 Suggested Promo Codes for Your Campaign</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h4 className="font-bold text-purple-700">LAUNCH50</h4>
              <p className="text-sm text-gray-600">50% off subscription for first 3 months</p>
              <p className="text-xs text-gray-400 mt-1">Great for new user acquisition</p>
            </div>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h4 className="font-bold text-blue-700">FREESETUP</h4>
              <p className="text-sm text-gray-600">Free setup fee ($150 value)</p>
              <p className="text-xs text-gray-400 mt-1">Remove barrier to entry</p>
            </div>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h4 className="font-bold text-emerald-700">VIP2025</h4>
              <p className="text-sm text-gray-600">Free everything for VIP partners</p>
              <p className="text-xs text-gray-400 mt-1">For strategic partnerships</p>
            </div>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <h4 className="font-bold text-amber-700">EARLYBIRD</h4>
              <p className="text-sm text-gray-600">25% off forever</p>
              <p className="text-xs text-gray-400 mt-1">Reward early adopters</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PromoCodeManager;
